import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import rateToolsService from '../../common/api/rateToolsService';
import { SEASON_RATE_BATCH_OPERATION_TYPE } from '../../common/constants';

export interface CopySeasonRatesToNextSalesPeriodRequest {
  accommodationId?: string;
  tagId?: string;
  copySeasonsTo: string;
}

export interface CopySeasonRatesFromAccommodationToAnotherRequest {
  fromAccommodationId: string;
  toAccommodationId: string;
  copyPrices: boolean;
  copyRules: boolean;
  useRulePrices: boolean;
  copySeasonsTo?: string;
  copyOnlyFutureSeasons?: boolean;
}

export interface ChangePricesForSeasonRatesRequest {
  accommodationId?: string;
  tagId?: string;
  changePricesFrom: string;
  changePricesTo: string;
  priceChangePercent: number;
  priceChangeAbsolute: number;
  updatePrices: boolean;
  updateVatPurchasePrices: boolean;
  updateWithoutVatPurchasePrices: boolean;
}

export interface ModifyDatesForSeasonRatesRequest {
  accommodationId?: string;
  tagId?: string;
  existingSeasonRateStartDate: string;
  existingSeasonRateEndDate: string;
  newSeasonRateStartDate: string;
  newSeasonRateEndDate: string;
  deleteOverlappingSeasons: boolean;
}

export interface ModifyAdditionalServiceRulesRequest {
  tagId: string;
  addonServiceId: string;
  isDelete: boolean;
  modifyRulesFrom: string;
  modifyRulesTo: string;
  priceChangePercentValue?: number;
  priceChangeAbsoluteValue?: number;
  isMandatory: boolean;
}

export interface RevertSeasonRateCopyRequest {
  batchUpdateDetailId: string;
  force: boolean;
}

export interface RevertSeasonRateDateChangeRequest {
  batchUpdateDetailId: string;
  force: boolean;
}

export interface RevertResultDto {
  revertedCount: number;
  conflictCount: number;
  conflictAccommodationNumbers: (number | null)[];
  revertBatchUpdateDetailId: string;
}

export interface RevertPreviewConflictDto {
  accommodationNumber: number | null;
  accommodationName: string | null;
  seasonStartDate: string;
  seasonEndDate: string;
  conflictReason: string;
}

export interface SeasonRateNotCopiedRecordDto {
  seasonName: string;
  startDate: string;
  endDate: string;
  sourceStartDate?: string;
  sourceEndDate?: string;
}

export interface SeasonRatesUpdateDetailDto {
  accommodationNumber?: number;
  accommodationName?: string;
  seasonRatesChangedCount: number;
  seasonRatesNotCopiedCount: number;
  notCopiedSeasons: SeasonRateNotCopiedRecordDto[];
}

export interface SeasonRateBatchUpdateDetailDto {
  id: string;
  operationDate: string;
  operationType: SEASON_RATE_BATCH_OPERATION_TYPE;
  requestParametersDescription?: string;
  accommodationsCopiedCount: number;
  accommodationsNotCopiedCount: number;
  updateDetails: SeasonRatesUpdateDetailDto[];
  isRevertible: boolean;
  revertedAt?: string;
}

export interface RevertPreviewDto {
  batchUpdateDetailId: string;
  operationType: SEASON_RATE_BATCH_OPERATION_TYPE;
  isRevertible: boolean;
  totalAffectedCount: number;
  conflictCount: number;
  conflicts: RevertPreviewConflictDto[];
}

export interface LastTagOperationDto {
  operationDate: string;
  operationType: SEASON_RATE_BATCH_OPERATION_TYPE;
  requestParametersDescription?: string;
  copiedUpToDate?: string;
}

export const copySeasonRatesToNextSalesPeriod = createAsyncThunk(
  'rateTools/copySeasonRatesToNextSalesPeriod',
  async (
    request: CopySeasonRatesToNextSalesPeriodRequest,
    { rejectWithValue },
  ) => {
    try {
      return await rateToolsService.copySeasonRatesToNextSalesPeriodRequest(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const copySeasonRatesFromAccommodationToAnother = createAsyncThunk(
  'rateTools/copySeasonRatesFromAccommodationToAnother',
  async (
    request: CopySeasonRatesFromAccommodationToAnotherRequest,
    { rejectWithValue },
  ) => {
    try {
      return await rateToolsService.copySeasonRatesFromAccommodationToAnotherRequest(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const changePricesForSeasonRates = createAsyncThunk(
  'rateTools/changePricesForSeasonRates',
  async (request: ChangePricesForSeasonRatesRequest, { rejectWithValue }) => {
    try {
      return await rateToolsService.doChangePricesForSeasonRates(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const modifyDatesForSeasonRates = createAsyncThunk(
  'rateTools/modifyDatesForSeasonRates',
  async (request: ModifyDatesForSeasonRatesRequest, { rejectWithValue }) => {
    try {
      return await rateToolsService.doModifyDatesForSeasonRates(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const modifyAdditionalServiceRulesForSeasonRates = createAsyncThunk(
  'rateTools/modifyAdditionalServiceRulesForSeasonRates',
  async (request: ModifyAdditionalServiceRulesRequest, { rejectWithValue }) => {
    try {
      return await rateToolsService.doModifyAdditionalServiceRulesForSeasonRates(
        request,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchSeasonRateBatchUpdateDetails = createAsyncThunk(
  'rateTools/fetchSeasonRateBatchUpdateDetails',
  async (
    operationType: SEASON_RATE_BATCH_OPERATION_TYPE,
    { rejectWithValue },
  ) => {
    try {
      return await rateToolsService.getSeasonRateBatchUpdateDetails(
        operationType,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const revertSeasonRateCopy = createAsyncThunk(
  'rateTools/revertSeasonRateCopy',
  async (request: RevertSeasonRateCopyRequest, { rejectWithValue }) => {
    try {
      return await rateToolsService.revertSeasonRateCopy(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const revertSeasonRateDateChange = createAsyncThunk(
  'rateTools/revertSeasonRateDateChange',
  async (request: RevertSeasonRateDateChangeRequest, { rejectWithValue }) => {
    try {
      return await rateToolsService.revertSeasonRateDateChange(request);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchRevertPreview = createAsyncThunk(
  'rateTools/fetchRevertPreview',
  async (batchUpdateDetailId: string, { rejectWithValue }) => {
    try {
      return await rateToolsService.getRevertPreview(batchUpdateDetailId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchLastTagOperation = createAsyncThunk(
  'rateTools/fetchLastTagOperation',
  async (
    {
      tagId,
      operationType,
    }: { tagId: string; operationType: SEASON_RATE_BATCH_OPERATION_TYPE },
    { rejectWithValue },
  ) => {
    try {
      return await rateToolsService.getLastBatchOperationForTag(
        tagId,
        operationType,
      );
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const rateToolsSlice = createSlice({
  name: 'rateTools',
  initialState: {
    seasonRateBatchUpdateDetails: [] as SeasonRateBatchUpdateDetailDto[],
  },
  reducers: {
    clearSeasonRateBatchUpdateDetails: state => {
      state.seasonRateBatchUpdateDetails = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(
      fetchSeasonRateBatchUpdateDetails.fulfilled,
      (state, action) => {
        state.seasonRateBatchUpdateDetails = action.payload;
      },
    );
  },
});

export const { clearSeasonRateBatchUpdateDetails } = rateToolsSlice.actions;

export default rateToolsSlice.reducer;
